/**
 * @format
 */
import { inject, Injectable } from '@angular/core';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { PlanValidationResult, ValidatablePlan } from 'common-typescript';
import {
    Attainment,
    CourseUnit,
    CourseUnitSelection,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    DPSMBase,
    Education,
    EntityWithRule,
    GradeRaiseAttempt,
    OtmId,
    StudyRight,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { ModalService } from 'sis-common';
import { customAttainmentCreditInfoModalOpener } from 'sis-components';
import { SelectCompletionMethodComponent } from '../../plan/select-completion-method/select-completion-method.component';
import { StudentCustomStudyDraftDialogComponent } from '../../plan/student-custom-study-draft-modal/student-custom-study-draft-dialog/student-custom-study-draft-dialog.component';
import {
    StudentCustomStudyDraftModalComponent,
    StudentCustomStudyDraftOptions,
} from '../../plan/student-custom-study-draft-modal/student-custom-study-draft-modal.component';
import { studentCourseUnitInfoModalOpener } from '../components/course-unit-info-modal/student-course-unit-info-modal/student-course-unit-info-modal.component';
import { studentModuleInfoModalOpener } from '../components/module-info/student-module-info-modal/student-module-info-modal.component';
import { createCustomStudyDraftModalOpener } from '../components/plan-common/create-custom-study-draft-modal/create-custom-study-draft-modal.component';
import { planStructureEditModalOpener } from '../components/plan-common/plan-structure-edit-modal/plan-structure-edit-modal.component';
import { AttainmentStudentModalService } from './attainment-student-modal-service';

@Injectable({
    providedIn: 'root',
})
export class StudentPlanUiActionsServiceImplementation {
    modalService = inject(ModalService);
    dialogService = inject(FudisDialogService);
    attainmentModalService = inject(AttainmentStudentModalService);

    private planStructureEditModalOpener = planStructureEditModalOpener();
    private createCustomStudyDraftModalOpener = createCustomStudyDraftModalOpener();
    private courseUnitInfoModalOpener = studentCourseUnitInfoModalOpener();
    private moduleInfoModalOpener = studentModuleInfoModalOpener();
    private customAttainmentCreditInfoModalOpener = customAttainmentCreditInfoModalOpener();

    openModuleHandler(
        module: EntityWithRule,
        validatablePlan: ValidatablePlan,
        matchingStudyRight: StudyRight,
        education: Education,
        planValidationResult: PlanValidationResult,
        allStudentStudyRights: StudyRight[],
        dismissFocusTarget?: HTMLElement,
    ) {
        return this.planStructureEditModalOpener({
            module,
            validatablePlan,
            education,
            planValidationResult,
            validatablePlanStudyRight: matchingStudyRight,
            allStudentStudyRights,
            dismissFocusTarget,
        });
    }

    openCustomStudyDraftInfoModalHandler(customStudyDraft: CustomStudyDraft, studentId: OtmId, options: StudentCustomStudyDraftOptions) {
        const customStudyDraftModalValues = { studentId, customStudyDraft, options };
        return this.modalService.open(StudentCustomStudyDraftModalComponent, customStudyDraftModalValues, { size: 'sm' });
    }

    openCustomStudyDraftInfoDialogHandler(customStudyDraft: CustomStudyDraft, studentId: OtmId, options: StudentCustomStudyDraftOptions) {
        const customStudyDraftDialogValues = { studentId, customStudyDraft, options };
        return this.dialogService.open(StudentCustomStudyDraftDialogComponent, { data: customStudyDraftDialogValues });
    }

    openCustomAttainmentInfoModalHandler(customAttainment: CustomCourseUnitAttainment | CustomModuleAttainment) {
        return this.customAttainmentCreditInfoModalOpener(customAttainment);
    }

    openCourseUnitInfoModalHandler(
        courseUnit: CourseUnit,
        validatablePlan: ValidatablePlan,
        studyRight: StudyRight,
        allowEdit: boolean = false,
    ) {
        return this.courseUnitInfoModalOpener({
            courseUnit,
            validatablePlan,
            studyRight,
            allowEdit,
        });
    }

    openSelectCompletionMethodModalHandler(
        courseUnit: CourseUnit,
        plannedCourseUnitId: OtmId,
        validatablePlan: ValidatablePlan,
        isGradeRaiseAttempt: boolean,
    ) {
        const courseUnitSelection = validatablePlan.getCourseUnitSelection(plannedCourseUnitId) as CourseUnitSelection;
        const currentAttainmentId = validatablePlan.getCourseUnitAttainment(plannedCourseUnitId)?.id;

        const { assessmentItemSelections, completionMethodId, gradeRaiseAttempt } = this.getCompletionMethodModalParams(
            courseUnit,
            validatablePlan,
            courseUnitSelection,
            isGradeRaiseAttempt,
        );

        return this.modalService.open(
            SelectCompletionMethodComponent,
            {
                assessmentItemSelections,
                currentAttainmentId,
                isGradeRaiseAttempt,
                completionMethodId,
                courseUnit,
                validatablePlan,
                gradeRaiseAttempt,
                plannedCourseUnitId,
            },
            { size: 'sm' },
        );
    }

    private getCompletionMethodModalParams(
        courseUnit: CourseUnit,
        validatablePlan: ValidatablePlan,
        courseUnitSelection: CourseUnitSelection,
        isGradeRaiseAttempt: boolean,
    ) {
        if (courseUnitSelection?.gradeRaiseAttempt) {
            const gradeRaiseAttempt = courseUnitSelection.gradeRaiseAttempt;
            return {
                assessmentItemSelections: gradeRaiseAttempt.selectedAssessmentItemIds,
                completionMethodId: gradeRaiseAttempt.completionMethodId,
                gradeRaiseAttempt,
            };
        }

        return {
            assessmentItemSelections: isGradeRaiseAttempt ? [] : _.map(validatablePlan.getSelectedAssessmentItems(courseUnit), ai => ai.id),
            completionMethodId: isGradeRaiseAttempt ? null : courseUnitSelection?.completionMethodId,
        };
    }

    openOpenAttainmentDetailsModalHandler(attainment: Attainment, gradeRaiseAttempt: GradeRaiseAttempt) {
        return this.attainmentModalService.openAttainmentDetailsModal(attainment, true, false, gradeRaiseAttempt);
    }

    openModuleInfoModalHandler(
        module: EntityWithRule,
        validatablePlan: ValidatablePlan,
        matchingStudyRight: StudyRight,
        planValidationResult: PlanValidationResult,
    ) {
        const isModuleInPlan = validatablePlan?.isModuleInPlan(module.id) || false;
        return this.moduleInfoModalOpener({
            module: module as unknown as DPSMBase,
            validatablePlan: isModuleInPlan ? validatablePlan : null,
            studyRight: matchingStudyRight,
            planValidationResult: isModuleInPlan ? planValidationResult : null,
        });
    }

    openCreateCustomStudyDraftModalHandler(selectedModuleId: OtmId) {
        return this.createCustomStudyDraftModalOpener({ selectedModuleId });
    }
}
